import React, { useContext } from "react";
import styled from "styled-components";
import { DarkModeContext } from "../../../../../appContexts/DarkModeContext";
import { renderCount } from "../../../../../appHelpers/appLoggers";
import { Tooltip, Whisper } from "rsuite";
import { UserContext } from "../../_context/UserContext";
import { useHistory } from "react-router-dom";
import { GlassCard, SectionRibbon, Button, colors } from "../../../../ui";

export const MachineMetadataFC = ({ metadata }) => {
  renderCount("MachineMetadataFC");
  const history = useHistory();
  const { darkMode } = useContext(DarkModeContext);
  const { userRoles } = useContext(UserContext);

  const speaker = <Tooltip>Avante ID: {metadata?.id}</Tooltip>;

  return (
    <GlassCard animated visible={true}>
      {/* Header Section with System Name and Net Admin Button */}
      <HeaderSection>
        <SystemInfo>
          <SystemName>📍 {metadata.name}</SystemName>
          <RoomInfo>Room: {metadata.room ? metadata.room : "N/A"}</RoomInfo>
        </SystemInfo>

        {(userRoles.includes("superuser") || userRoles.includes("network")) && (
          <NetAdminSection>
            <Button
              variant="secondary"
              size="small"
              onClick={() => history.push(`/admin/system/${metadata.id}`)}
            >
              ⚙️ Net Admin
            </Button>
          </NetAdminSection>
        )}
      </HeaderSection>

      <ModernDivider />

      {/* System Details Grid */}
      <MetadataGrid>
        <MetadataItem>
          <MetadataLabel>ID</MetadataLabel>
          {(() => {
            const idLabel = metadata?.cus_sys_id ?? metadata?.id;
            const value = metadata?.system_link ? (
              <MetadataLink
                href={metadata.system_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {idLabel}
                <LinkIndicator aria-hidden="true">↗</LinkIndicator>
              </MetadataLink>
            ) : (
              <MetadataValue>{idLabel}</MetadataValue>
            );

            return metadata?.cus_sys_id ? (
              <Whisper placement="bottom" trigger="hover" speaker={speaker}>
                {value}
              </Whisper>
            ) : (
              value
            );
          })()}
        </MetadataItem>

        <MetadataItem>
          <MetadataLabel>SYSTEM</MetadataLabel>
          <MetadataValue>
            {metadata?.manufacturer} • {metadata?.modality}
          </MetadataValue>
        </MetadataItem>

        <MetadataItem>
          <MetadataLabel>MODEL</MetadataLabel>
          <MetadataValue>{metadata?.model}</MetadataValue>
        </MetadataItem>

        <MetadataItem>
          <MetadataLabel>SERIAL#</MetadataLabel>
          <MetadataValue>
            {metadata?.serial_number ? metadata.serial_number : "N/A"}
          </MetadataValue>
        </MetadataItem>

        <MetadataItem>
          <MetadataLabel>S/V</MetadataLabel>
          <MetadataValue>
            {metadata?.software_version ? metadata.software_version : "N/A"}
          </MetadataValue>
        </MetadataItem>
      </MetadataGrid>
    </GlassCard>
  );
};

// TODO: 1 - MAYBE FIND EXACT WIDTH TO MATCH TABLE BELOW 2 - SET COLOR(S) TO A THEME VALUE
// Modern Styled Components
const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const SystemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const SystemName = styled.div`
  font-size: 1.1em;
  font-weight: 600;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  gap: 8px;

  @media screen and (max-width: 768px) {
    font-size: 1em;
  }

  @media screen and (min-width: 1401px) {
    font-size: 1.3em;
  }
`;

const RoomInfo = styled.div`
  font-size: 0.9em;
  font-weight: 500;
  color: ${colors.neutral[600]};

  @media screen and (max-width: 768px) {
    font-size: 0.85em;
  }

  @media screen and (min-width: 769px) {
    font-size: 0.95em;
  }
`;

const NetAdminSection = styled.div`
  display: flex;
  align-items: flex-start;

  @media screen and (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const ModernDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    ${colors.neutral[300]},
    transparent
  );
  margin: 16px 0;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 16px;

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 12px;
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0.08) 100%
    );
    border-color: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media screen and (max-width: 480px) {
    padding: 10px 6px;
    gap: 6px;
  }
`;

const MetadataLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${colors.neutral[600]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 0.7rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.65rem;
  }
`;

const MetadataValue = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  color: ${colors.primary.main};
  cursor: ${(props) => (props.onClick ? "pointer" : "default")};
  word-break: break-word;

  @media screen and (max-width: 768px) {
    font-size: 0.85rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const MetadataLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  color: ${colors.primary.main};
  cursor: pointer;
  word-break: break-word;
  text-decoration: underline;

  &:hover {
    color: ${colors.primary.dark};
  }

  @media screen and (max-width: 768px) {
    font-size: 0.85rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const LinkIndicator = styled.span`
  font-size: 0.75em;
  line-height: 1;
  text-decoration: none;
  opacity: 0.8;
`;
